import { Suspense, useEffect, useMemo, useState } from 'react';
import { type FieldValues, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { isEmpty, isNil } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import tzlookup from 'tz-lookup';
import { ROUTES } from '../../constants/Constants';
import { lazyReactNaiveRetry } from '../../constants/Lazy';
import useAppNavigation from '../../hooks/useAppNavigation';
import useJoinModal from '../../hooks/useJoinModal';
import useUser from '../../hooks/useUser';
import type { CreatePartnerServiceDescriptorType } from '../../proxies/PartnerProxy';
import { locationToAddressRequest } from '../../redux/ducks/MapDucks';
import { createPartnerRequest } from '../../redux/ducks/PartnerDucks';
import type {
  CreatePartnerBusinessDescriptorType,
  CreatePartnerBusinessLocationDescriptorType,
  CreatePartnerBusinessOpeningDescriptorType,
  CreatePartnerRequestType,
} from '../../redux/ducks/PartnerDucks.types';
import type { SelectedPlaceType } from '../../redux/ducks/StorageDucks.types';
import { setJoinModalOpen } from '../../redux/ducks/UIDucks';
import type { RootReducerType } from '../../redux/reducers/RootReducer.types';
import { trackEvent } from '../../utils/Umami/Umami';
import type { UserServiceType } from '../inputs/AddService';
import type { BusinessDayType } from '../inputs/BusinessDay';
import { DefaultBusinessHours } from '../inputs/BusinessHours';
import Heading from '../layout/Heading';
import type { MarkerProps } from '../layout/Map';
import Spinner from '../layout/Spinner/Spinner';
import Modal from './Modal';

const CustomMap = lazyReactNaiveRetry(() => import('../layout/Map'));
const PlaceSelect = lazyReactNaiveRetry(() => import('../inputs/PlaceSelect'));
const BusinessHours = lazyReactNaiveRetry(
  () => import('../inputs/BusinessHours'),
);
const AddService = lazyReactNaiveRetry(() => import('../inputs/AddService'));
const PartnerCreationSuccess = lazyReactNaiveRetry(
  () => import('../inputs/PartnerCreationSuccess'),
);

enum STEPS {
  LOCATION = 0,
  HOURS = 1,
  SERVICES = 2,
  SUCCESS = 3,
}

const JoinModal = () => {
  const joinModal = useJoinModal();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentUser } = useUser();
  const { navigate, toLocalizedRoute } = useAppNavigation();

  const locationToAddressResponse = useSelector(
    (state: RootReducerType) => state.map.locationToAddressResponse,
  );
  const createPartnerResponse = useSelector(
    (state: RootReducerType) => state.partner.createPartnerResponse,
  );

  const [step, setStep] = useState(STEPS.LOCATION);
  const [locationMarker, setLocationMarker] = useState<
    MarkerProps | undefined
  >();

  const { setValue, reset, watch } = useForm<FieldValues>({
    defaultValues: {
      tag: null,
      location: null,
      hours: null,
      service: null,
    },
  });

  const tag: string | null = watch('tag');
  const location: SelectedPlaceType | null = watch('location');
  const hours: BusinessDayType[] | null = watch('hours');
  const service: UserServiceType | null = watch('service');

  useEffect(() => {
    if (!joinModal.isOpen) {
      reset();
    }
  }, [joinModal.isOpen]);

  useEffect(() => {
    if (isNil(location?.latlng)) {
      setLocationMarker(undefined);
    } else {
      setLocationMarker({
        id: 'add-partner-map-marker',
        active: true,
        lat: location?.latlng[0] || 0.0,
        lng: location?.latlng[1] || 0.0,
      });
    }
  }, [location]);

  const setCustomValue = (id: string, value: any) => {
    setValue(id, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const onClose = () => {
    setStep(STEPS.LOCATION);
    joinModal.onClose();
  };

  const onBack = () => {
    setStep((value) => value - 1);
  };

  const onNewPartner = () => {
    const serviceDesc: CreatePartnerServiceDescriptorType = {
      name: service?.name || '',
      price: service?.price || 0,
      currency: service?.currency || '',
      duration: service?.duration ? service.duration * 60 : 0,
    };
    if (tag) {
      serviceDesc.tag = tag;
    }

    const businessLocationDesc: CreatePartnerBusinessLocationDescriptorType = {
      address:
        location?.address?.enteredAddress ||
        location?.address?.positionAddress ||
        '',
      city: location?.city?.label || '',
      state: location?.state?.label,
      country: location?.country?.value || '',
      zipcode: location?.address?.zipcode,
      latitude: location?.address?.latlng ? location?.address?.latlng[0] : 0,
      longitude: location?.address?.latlng ? location?.address?.latlng[1] : 0,
    };

    const businessTimezone = tzlookup(
      businessLocationDesc.latitude,
      businessLocationDesc.longitude,
    );

    const businessHoursDesc: CreatePartnerBusinessOpeningDescriptorType[] = [];
    if (hours) {
      for (let x = 0; x < hours.length; x++) {
        const hour = hours[x];
        for (let y = 0; y < hour.openings.length; y++) {
          const opening = hour.openings[y];

          const hourDesc: CreatePartnerBusinessOpeningDescriptorType = {
            day: hour.dayNumber + 1,
            start: opening.start,
            end: opening.end,
          };

          businessHoursDesc.push(hourDesc);
        }
      }
    }

    const businessDesc: CreatePartnerBusinessDescriptorType = {
      timezone: businessTimezone,
      openings: businessHoursDesc,
      location: businessLocationDesc,
    };

    const newPartnerRequest: CreatePartnerRequestType = {
      service: serviceDesc,
      business: businessDesc,
    };

    dispatch(createPartnerRequest(newPartnerRequest));
  };

  const onSuccessfulCreation = () => {
    joinModal.onClose();

    if (createPartnerResponse?.data?.approved) {
      navigate(toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_PROFILE));
    } else {
      navigate(toLocalizedRoute(ROUTES.HOME_ROUTE));
    }
  };

  const onNext = () => {
    // call umami helper programatically to track the subscription funnel
    trackEvent(`join-modal-step-${step}-complete`, 1);

    if (step === STEPS.SERVICES) {
      onNewPartner();
      setStep((value) => value + 1);
    } else if (step === STEPS.SUCCESS) {
      setStep(STEPS.LOCATION);
      onSuccessfulCreation();
    } else {
      setStep((value) => value + 1);
    }
  };

  const actionLabel = useMemo(() => {
    if (step === STEPS.SERVICES) {
      return t('static.join.modal_create_btn_label');
    }

    if (step === STEPS.SUCCESS) {
      return t('static.join.modal_success_btn_label');
    }

    return t('static.join.modal_next_btn_label');
  }, [step, t]);

  const secondaryActionLabel = useMemo(() => {
    if (step === STEPS.LOCATION) {
      return undefined;
    }

    return t('static.join.modal_back_btn_label');
  }, [step, t]);

  const actionDisabled: boolean = useMemo(() => {
    if (step === STEPS.LOCATION) {
      return isEmpty(location?.address?.latlng);
    }

    if (step === STEPS.SERVICES) {
      return isNil(service?.valid) || service?.valid !== true;
    }

    if (step === STEPS.SUCCESS) {
      return isNil(currentUser) || currentUser.partner !== true;
    }

    return false;
  }, [step, location, service, currentUser]);

  const secondaryActionDisabled: boolean = useMemo(() => {
    if (step === STEPS.SUCCESS) {
      return isNil(createPartnerResponse) || isNil(createPartnerResponse.error);
    }

    return false;
  }, [step, createPartnerResponse]);

  const onMapClick = (event: any) => {
    if (event?.latlng) {
      setLocationMarker({
        id: 'add-partner-map-marker',
        active: true,
        lat: event?.latlng?.lat,
        lng: event?.latlng?.lng,
      });

      dispatch(
        locationToAddressRequest({
          countrycodes: location?.country?.value || '',
          lat: event?.latlng?.lat || 0.0,
          lon: event?.latlng?.lng || 0.0,
        }),
      );
    }
  };

  const map = useMemo(() => {
    if (!joinModal || !joinModal.isOpen) {
      return null;
    }

    const zoomLevel =
      location?.latlng || location?.address?.latlng
        ? 16
        : location?.city
          ? 13
          : location?.state
            ? 10
            : location?.country
              ? 4
              : 2;
    return (
      <Suspense
        fallback={
          <div className="flex size-full flex-col items-center justify-center">
            <Spinner color="fill-sky-600" />
          </div>
        }
      >
        <CustomMap
          id="join-modal-step-map"
          center={
            location?.latlng ||
            location?.city?.latlng ||
            location?.state?.latlng ||
            location?.country?.latlng
          }
          styled="rounded-lg"
          zoom={zoomLevel}
          readOnly={false}
          scrollWheelZoom
          onMapClick={onMapClick}
          markers={locationMarker ? [locationMarker] : []}
          autoFit={false}
        />
      </Suspense>
    );
  }, [location, joinModal, locationMarker]);

  useEffect(() => {
    if (locationToAddressResponse?.data) {
      if (location) {
        let formattedAddress = null;
        if (locationToAddressResponse.data.address?.house_number) {
          formattedAddress =
            locationToAddressResponse.data.address?.house_number;
        }

        if (locationToAddressResponse.data.address?.road) {
          formattedAddress = !isEmpty(formattedAddress)
            ? `${formattedAddress}, ${locationToAddressResponse.data.address?.road}`
            : locationToAddressResponse.data.address?.road;
        }

        formattedAddress =
          locationToAddressResponse.data.address?.road ||
          locationToAddressResponse.data.address?.suburb ||
          locationToAddressResponse.data.name;

        const newLocation: SelectedPlaceType = {
          ...location,
          address: {
            enteredAddress: location?.address?.enteredAddress,
            positionAddress: formattedAddress,
            zipcode: locationToAddressResponse.data.address?.postcode,
            latlng: [
              locationToAddressResponse.data.lat,
              locationToAddressResponse.data.lon,
            ],
          },
          latlng: [
            locationToAddressResponse.data.lat,
            locationToAddressResponse.data.lon,
          ],
        };

        setValue('location', newLocation);
      }
    }
  }, [locationToAddressResponse]);

  let bodyContent = (
    <div className="flex flex-col gap-8">
      <Heading title={t('static.join.modal_step_location_heading_title')} />
      <Suspense
        fallback={
          <div className="flex size-full flex-col items-center justify-center">
            <Spinner color="fill-sky-600" />
          </div>
        }
      >
        <span className="-mt-2 sm:mt-4">
          <PlaceSelect
            data-test-id="place-selection-component"
            value={location || undefined}
            onChange={(value) => setCustomValue('location', value)}
            addressRequired
          />
        </span>
      </Suspense>
      <div className="aspect-square w-full md:aspect-video">{map}</div>
    </div>
  );

  if (step === STEPS.HOURS) {
    bodyContent = (
      <div className="flex flex-col gap-8">
        <Heading
          title={t('static.join.modal_step_hours_heading_title')}
          subtitle={t('static.join.modal_step_hours_heading_subtitle')}
        />
        <Suspense
          fallback={
            <div className="flex size-full flex-col items-center justify-center">
              <Spinner color="fill-sky-600" />
            </div>
          }
        >
          <BusinessHours
            data-test-id="biz-hours"
            hours={hours || JSON.parse(JSON.stringify(DefaultBusinessHours))}
            onChange={(value) => setCustomValue('hours', value)}
          />
        </Suspense>
      </div>
    );
  }

  if (step === STEPS.SERVICES) {
    bodyContent = (
      <div className="flex flex-col gap-8">
        <Heading
          title={t('static.join.modal_step_service_heading_title')}
          subtitle={t('static.join.modal_step_service_heading_subtitle')}
        />
        <Suspense
          fallback={
            <div className="flex size-full flex-col items-center justify-center">
              <Spinner color="fill-sky-600" />
            </div>
          }
        >
          <AddService
            data-test-id="add-service"
            service={service || undefined}
            currency={
              service?.currency ||
              (location?.country?.currency
                ? location?.country.currency
                : undefined)
            }
            onChange={(value) => {
              setCustomValue('service', value);
            }}
            onSubmit={false}
          />
        </Suspense>
      </div>
    );
  }

  if (step === STEPS.SUCCESS) {
    bodyContent = (
      <div className="flex size-full flex-col items-center justify-center">
        <Suspense
          fallback={
            <div className="flex size-full flex-col items-center justify-center">
              <Spinner color="fill-sky-600" />
            </div>
          }
        >
          <PartnerCreationSuccess data-test-id="success-partner-component" />
        </Suspense>
      </div>
    );
  }

  return (
    <Modal
      id="JoinModal"
      setIsOpen={(open) => dispatch(setJoinModalOpen(open))}
      title={t('static.join.modal_title')}
      actionLabel={actionLabel}
      disabled={actionDisabled}
      secondaryActionLabel={secondaryActionLabel}
      secondaryActionDisabled={secondaryActionDisabled}
      secondaryAction={step === STEPS.LOCATION ? undefined : onBack}
      isOpen={joinModal.isOpen}
      onClose={onClose}
      onSubmit={onNext}
      body={bodyContent}
    />
  );
};

export default JoinModal;
